import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowRight, CheckCircle, Loader2, Zap, Target, TrendingUp } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api/client';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

export default function LoginPage() {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => localStorage.getItem('scout_email') || '');
  const isReturning = !!localStorage.getItem('scout_email');

  const { isAuthenticated, isLoading, error, magicLinkSent, requestMagicLink, verifyMagicLink, clearError } =
    useAuthStore();

  // Capture referral code from URL (?ref=SC-XXXXX) and store in localStorage
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      localStorage.setItem('scout_referralCode', ref);
    }
  }, [searchParams]);

  // Check for magic link token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyMagicLink(token);
    }
  }, [searchParams, verifyMagicLink]);

  // Redirect if authenticated (check for pending QR code from visitor CTA, then accept invite, then redirect param)
  useEffect(() => {
    if (!isAuthenticated) return;

    const pendingQr = localStorage.getItem('scout_pendingQrCode');
    if (pendingQr) {
      localStorage.removeItem('scout_pendingQrCode');
      navigate(`/scan/${pendingQr}`, { replace: true });
      return;
    }

    // Auto-accept invitation if accept param is present (from invite email)
    const acceptId = searchParams.get('accept');
    if (acceptId) {
      api.patch(`/scout/memberships/${acceptId}/accept`).catch((err) => {
        console.error('Failed to accept membership invitation:', err);
      });
    }

    const redirect = searchParams.get('redirect');
    navigate(redirect || '/', { replace: true });
  }, [isAuthenticated, navigate, searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    if (email.trim()) {
      const referralCode = localStorage.getItem('scout_referralCode') || undefined;
      await requestMagicLink(email.trim(), referralCode);
    }
  };

  // Show verifying/error state when token is in URL
  const hasToken = !!searchParams.get('token');
  if (hasToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-primary/5 to-white px-4">
        <div className="w-full max-w-sm text-center">
          {error ? (
            <>
              <div className="w-16 h-16 rounded-full bg-critical/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-critical" />
              </div>
              <h2 className="text-section font-bold text-foreground mb-2">{t('login.verifyFailed')}</h2>
              <p className="text-small text-muted-foreground mb-6">{error}</p>
              <button
                onClick={() => {
                  clearError();
                  navigate('/login', { replace: true });
                }}
                className="text-small text-primary hover:text-forest font-medium"
              >
                {t('login.tryAgain')}
              </button>
            </>
          ) : (
            <>
              <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
              <h2 className="text-section font-bold text-foreground mb-2">{t('login.verifying')}</h2>
              <p className="text-small text-muted-foreground">{t('login.verifyingHint')}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (magicLinkSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-primary/5 to-white px-4">
        <div className="w-full max-w-sm text-center animate-fade-in">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-section font-bold text-foreground mb-2">{t('login.checkEmail')}</h2>
          <p className="text-small text-muted-foreground mb-2">{t('login.magicLinkSent', { email })}</p>
          <p className="text-caption text-muted-foreground mb-6">{t('login.checkSpam')}</p>
          <button
            onClick={() => useAuthStore.getState().resetMagicLinkSent()}
            className="text-small text-primary hover:text-forest font-medium"
          >
            {t('login.tryDifferentEmail')}
          </button>
        </div>
      </div>
    );
  }

  const benefits = [
    { icon: Zap, text: t('login.benefit1') },
    { icon: Target, text: t('login.benefit2') },
    { icon: TrendingUp, text: t('login.benefit3') },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-primary/5 to-white px-4 relative">
      {/* Language switcher */}
      <div className="absolute top-4 end-4 z-10">
        <LanguageSwitcher />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-sm mx-auto">
        {/* Hero */}
        <div className="text-center mb-6 animate-fade-in-up">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-forest flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-2xl text-white font-bold">S</span>
          </div>
          <h1 className="text-page-title text-foreground">{t('login.title')}</h1>
          <p className="text-small text-muted-foreground mt-1">
            {isReturning ? t('login.welcomeBack') : t('login.subtitle')}
          </p>
          {!isReturning && <p className="text-caption text-muted-foreground mt-2">{t('login.trustLine')}</p>}
        </div>

        {/* Value prop benefits (hidden for returning users) */}
        {!isReturning && (
          <div className="w-full mb-6 space-y-2.5 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white border border-warm-border shadow-sm"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-small font-medium text-foreground">{b.text}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Login form */}
        <form
          onSubmit={handleSubmit}
          className="w-full space-y-4 animate-fade-in-up"
          style={{ animationDelay: '200ms' }}
        >
          <div>
            <label htmlFor="email" className="block text-small font-medium text-foreground mb-1.5">
              {t('login.emailLabel')}
            </label>
            <div className="relative">
              <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                className="w-full h-12 ps-10 pe-4 rounded-xl border border-warm-border bg-white text-small focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                disabled={isLoading}
              />
            </div>
          </div>

          {error && <p className="text-small text-critical text-center">{error}</p>}

          <button
            type="submit"
            disabled={isLoading || !email.trim()}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-primary to-forest text-white font-medium text-small flex items-center justify-center gap-2 hover:from-forest hover:to-forest disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                {isReturning ? t('login.signIn') : t('login.sendLink')}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        <p className="text-caption text-muted-foreground text-center mt-4">{t('login.noPassword')}</p>
      </div>
    </div>
  );
}
